/**
 * Badges/indicadores da Tela 2 (Pessoas) — convertidos de
 * design-references/brief-2b/shared/badges.jsx (window.* → props/imports).
 * Consomem as classes de `pessoas-2b.css` + `.bond-badge--*` do DS.
 */

import { User, Shield, AlertTriangle, Clock, ShieldCheck } from 'lucide-react';

import { bondVisualGroup, riskBand } from './persons-adapter';

import type { GuardianshipIndicatorState, RiskLevel } from './persons-adapter';
import type { BondType } from '@lume/shared';

/**
 * Badge de risco derivado de `riskScore` (D1). NÃO é a sensibilidade LGPD N1-4
 * (essa não existe no contrato de Person) — rótulo honesto "Risco <faixa>".
 */
const RISK_VARIANT: Record<RiskLevel, string> = {
  baixo: 'badge--neutral',
  medio: 'badge--warning',
  alto: 'badge--warning',
  critico: 'badge--danger',
};

export function RiskBadge({ score }: { score: number }): JSX.Element {
  const band = riskBand(score);
  return (
    <span
      className={`badge ${RISK_VARIANT[band.level]}`}
      title={`Risco derivado do riskScore (${score.toFixed(0)}/100)`}
    >
      <span className="badge__dot" aria-hidden />
      {band.label}
    </span>
  );
}

const GUARDIANSHIP_META: Record<
  Exclude<GuardianshipIndicatorState, 'none'>,
  { cls: string; Icon: typeof User; title: string }
> = {
  minor: {
    cls: 'guardianship-indicator--minor',
    Icon: User,
    title: 'Menor representado por responsável legal',
  },
  ward: {
    cls: 'guardianship-indicator--ward',
    Icon: Shield,
    title: 'Incapaz representado por curador',
  },
  expired: {
    cls: 'guardianship-indicator--expired',
    Icon: AlertTriangle,
    title: 'Guardianship vencida',
  },
  pending: {
    cls: 'guardianship-indicator--pending',
    Icon: Clock,
    title: 'Guardianship pendente de verificação',
  },
};

export function GuardianshipIndicator({
  state,
  label,
}: {
  state: GuardianshipIndicatorState;
  label: string;
}): JSX.Element {
  if (state === 'none') {
    return (
      <span className="guardianship-indicator" title="Sem guardianship">
        <User size={11} />
        Sem guardianship
      </span>
    );
  }
  const meta = GUARDIANSHIP_META[state];
  const { Icon } = meta;
  return (
    <span className={`guardianship-indicator ${meta.cls}`} title={meta.title}>
      <Icon size={11} />
      {label}
    </span>
  );
}

export function LegalCapacityIndicator({ label }: { label: string }): JSX.Element {
  return (
    <span className="legal-capacity-indicator" title="Capacidade jurídica (Regulated)">
      <ShieldCheck size={11} />
      Capacidade jurídica: {label}
    </span>
  );
}

/**
 * Bond badge colorido por grupo visual (mapeia os 21 BondType nos 5 grupos do
 * `.bond-badge--*`). Diferente do BondBadge neutro da Inbox — aqui a cor importa
 * (Tela 2 mostra o leque de bonds da pessoa).
 */
export function PersonBondBadge({ type, label }: { type: BondType; label: string }): JSX.Element {
  const group = bondVisualGroup(type);
  return (
    <span className={`bond-badge bond-badge--${group}`} title={`Bond ${type} · ${label}`}>
      {label}
    </span>
  );
}
